import { SEO } from "./SEO";
import { CTA } from "./CTA";
import { Section, SectionHeader } from "./Section";
import { ComparisonChart } from "./ComparisonChart";
import { CTASection } from "./CTASection";
import InterventionQuadrants from "./InterventionQuadrants";
import type { DomainData } from "../lib/domains";
import { useTranslation } from "../lib/i18n";

interface DomainPageProps {
  domain: DomainData;
}

export function DomainPage({ domain }: DomainPageProps) {
  const { t } = useTranslation();
  return (
    <>
      <SEO
        title={`${domain.name} — Sentinel`}
        description={domain.tagline}
        canonical={`/domains/${domain.slug}`}
        image={domain.backgroundImage}
      />

      {/* 1. Hero Section */}
      <section
        style={{
          position: "relative",
          minHeight: "520px",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${domain.backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(90deg, rgba(10,22,40,0.82) 0%, rgba(10,22,40,0.55) 45%, rgba(10,22,40,0.25) 100%)",
          }}
          aria-hidden
        />
        <div className="container" style={{ position: "relative", zIndex: 1, padding: "var(--space-12) var(--space-4)" }}>
          <div className="eyebrow eyebrow-dark" style={{ marginBottom: "1rem" }}>
            {t("common.safetyDomain")}
          </div>
          <h1 style={{ color: "var(--text-on-dark)", marginBottom: "1rem", maxWidth: "720px" }}>
            {domain.headline}
          </h1>
          <p className="lead lead-dark" style={{ marginBottom: "2rem", maxWidth: "640px" }}>
            {domain.tagline}
          </p>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <CTA to="/register" variant="primary" size="lg">{t("cta.deploy")}</CTA>
            <CTA to="/downloads" variant="ghost-light" size="lg">{t("cta.download")}</CTA>
          </div>
        </div>
      </section>

      {/* 2. Current Safety Reality */}
      <Section>
        <SectionHeader
          eyebrow={t("section.currentSafety")}
          title={t("domainPage.currentTitle", { name: domain.name })}
        />
        <div className="container-narrow">
          <p style={{ fontSize: "1.125rem", lineHeight: 1.7, color: "var(--neutral-700)" }}>
            {domain.currentReality}
          </p>
          <div
            style={{
              marginTop: "2rem",
              borderRadius: "var(--radius-lg)",
              overflow: "hidden",
              boxShadow: "var(--shadow-md)",
            }}
          >
            <img
              src="https://images.pexels.com/photos/37769416/pexels-photo-37769416.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt={`${domain.name} current safety monitoring`}
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
        </div>
      </Section>

      {/* 3. Why Sentinel Is Necessary */}
      <Section variant="dark">
        <SectionHeader
          eyebrow={t("section.whySentinel")}
          title={t("domainPage.whyTitle")}
          dark
        />
        <div className="container-narrow">
          <p className="lead lead-dark" style={{ fontSize: "1.125rem" }}>
            {domain.whySentinel}
          </p>
          <div
            style={{
              marginTop: "2rem",
              borderRadius: "var(--radius-lg)",
              overflow: "hidden",
              boxShadow: "var(--shadow-lg)",
            }}
          >
            <img
              src="https://images.pexels.com/photos/32845695/pexels-photo-32845695.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt={`${domain.name} Sentinel safety operations`}
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
        </div>
      </Section>

      {/* 4. Comparison Chart */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("section.comparison")}
          title={t("domainPage.comparisonTitle")}
        />
        <ComparisonChart rows={domain.comparison} />
      </Section>

      {/* 5. Intervention Examples */}
      <Section>
        <SectionHeader
          eyebrow={t("section.interventions")}
          title={t("domainPage.interventionsTitle", { name: domain.name })}
        />
        <InterventionQuadrants interventions={domain.interventions} />
      </Section>

      {/* 6. Licensing Guidance */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("section.licensing")}
          title={t("domainPage.licensingTitle", { name: domain.name })}
        />
        <div className="grid grid-2">
          {domain.licensing.map((lic) => (
            <div key={lic.tier} className="card">
              <h3 style={{ fontSize: "1rem", color: "var(--accent-700)", marginBottom: "0.5rem" }}>
                {lic.tier}
              </h3>
              <p className="text-muted" style={{ fontSize: "0.9375rem" }}>{lic.guidance}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 7. Training & Certification */}
      <Section>
        <SectionHeader
          eyebrow={t("section.training")}
          title={t("domainPage.trainingTitle", { name: domain.name })}
        />
        <div className="grid grid-3">
          {domain.training.map((tr) => (
            <div key={tr.name} className="card card-hover">
              <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem" }}>{tr.name}</h3>
              <p className="text-muted" style={{ fontSize: "0.9375rem" }}>{tr.description}</p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: "var(--space-4)" }}>
          <h4 style={{ fontSize: "0.8125rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-600)", marginBottom: "0.75rem" }}>
            {t("section.certificationTracks")}
          </h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
            {domain.certification.map((cert) => (
              <span key={cert} className="badge badge-accent">{cert}</span>
            ))}
          </div>
        </div>
      </Section>

      {/* 8. Visuals */}
      <section
        style={{
          position: "relative",
          minHeight: "320px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${domain.backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, rgba(10,22,40,0.35) 0%, rgba(10,22,40,0.55) 100%)",
          }}
          aria-hidden
        />
        <div className="container" style={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <img
            src={domain.backgroundImage}
            alt={`${domain.name} safety`}
            style={{
              maxWidth: "100%",
              maxHeight: "280px",
              borderRadius: "var(--radius-xl)",
              boxShadow: "var(--shadow-xl)",
            }}
          />
        </div>
      </section>

      {/* 9. CTA */}
      <CTASection
        title={t("domainPage.ctaTitle", { name: domain.name })}
        description={t("domainPage.ctaDescription")}
        primaryLabel={t("cta.deploy")}
        primaryTo="/register"
        secondaryLabel={t("cta.joinCommunity")}
        secondaryTo="/community"
      />
    </>
  );
}
